import { useCallback, useMemo, useState } from 'react';
import { ColumnMeta, FilterValue } from './types';
import { getAtPath, isNoOpFilter, makeComparator, matchesFilter } from './comparators';

export interface TableControlsOptions {
  rows: any[];
  columns: ColumnMeta[];
  initialPageSize?: number;
  /** Server-reported total, when the list may be truncated by TABLE_FETCH_CAP. */
  totalCount?: number;
}

export function useTableControls(opts: TableControlsOptions) {
  const { rows, columns, initialPageSize = 20 } = opts;

  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const [filters, setFilters] = useState<Record<string, FilterValue>>({});
  const [page, setPageRaw] = useState(1);
  const [pageSize, setPageSizeRaw] = useState(initialPageSize);

  const colByKey = useMemo(() => new Map(columns.map(c => [c.key, c])), [columns]);

  const toggleSort = useCallback((key: string) => {
    setSort(prev =>
      !prev || prev.key !== key ? { key, dir: 'asc' }
        : prev.dir === 'asc' ? { key, dir: 'desc' }
        : null,
    );
  }, []);

  const setFilter = useCallback((key: string, value: FilterValue | undefined) => {
    setFilters(prev => {
      const next = { ...prev };
      if (isNoOpFilter(value)) delete next[key];
      else next[key] = value as FilterValue;
      return next;
    });
    setPageRaw(1);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    setPageRaw(1);
  }, []);

  const setPageSize = useCallback((n: number) => {
    setPageSizeRaw(n);
    setPageRaw(1);
  }, []);

  const setPage = useCallback((n: number) => {
    setPageRaw(Math.max(1, n));
  }, []);

  // filter → sort → paginate
  const filtered = useMemo(() => {
    const active = Object.entries(filters);
    if (active.length === 0) return rows;
    return rows.filter(row =>
      active.every(([key, f]) => {
        const col = colByKey.get(key);
        if (!col) return true;
        return matchesFilter(getAtPath(row, key), f);
      }),
    );
  }, [rows, filters, colByKey]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = colByKey.get(sort.key);
    if (!col) return filtered;
    return [...filtered].sort(makeComparator(col, sort.dir));
  }, [filtered, sort, colByKey]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, pageCount);

  const visibleRows = useMemo(
    () => sorted.slice((safePage - 1) * pageSize, safePage * pageSize),
    [sorted, safePage, pageSize],
  );

  const totalCount = opts.totalCount ?? rows.length;
  const activeFilterCount = Object.keys(filters).length;

  return {
    visibleRows,
    filteredCount: filtered.length,
    totalCount,
    loadedCount: rows.length,
    truncated: totalCount > rows.length,
    columns,
    getColumn: (key: string) => colByKey.get(key),
    sort,
    toggleSort,
    getFilter: (key: string) => filters[key],
    setFilter,
    clearAllFilters,
    activeFilterCount,
    page: safePage,
    pageCount,
    pageSize,
    setPage,
    setPageSize,
  };
}

export type TableControls = ReturnType<typeof useTableControls>;
